import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Container,
  Divider,
  Tooltip,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Stack,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SchoolIcon from '@mui/icons-material/School';
import LanguageIcon from '@mui/icons-material/Language';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';
import SourceIcon from '@mui/icons-material/Source';
import CodeIcon from '@mui/icons-material/Code';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BuildIcon from '@mui/icons-material/Build';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';

import { useGuide } from '../context/GuideContext';
import ContactModal from '../components/ContactModal';
import PromotionModal from '../components/PromotionModal';

import { getContactInfo } from '../utils/iframe';

const HomePage = () => {
  const navigate = useNavigate();
  const { startGuide } = useGuide();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [promotionModalOpen, setPromotionModalOpen] = useState(false);

  const contactInfo = getContactInfo();

  // 가이드 시작 핸들러
  const handleStartGuide = () => {
    startGuide('home');
  };

  // 모달 핸들러
  const handleContactModalOpen = () => {
    setContactModalOpen(true);
  };

  const handleContactModalClose = () => {
    setContactModalOpen(false);
  };

  // 프로모션 모달 핸들러
  const handlePromotionModalOpen = () => {
    setPromotionModalOpen(true);
  };

  const handlePromotionModalClose = () => {
    setPromotionModalOpen(false);
  };

  const handlePromotionContactClick = () => {
    setContactModalOpen(true);
  };

  // 페이지 로드 시 프로모션 모달 자동 표시 (3초 후)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPromotionModalOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Container maxWidth="lg">
      
      {/* Hero Section - 엔터프라이즈 프리미엄 디자인 */}
      <Box sx={{ 
        my: { xs: 3, md: 6 }, 
        textAlign: 'center', 
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.08) 0%, rgba(45, 55, 72, 0.08) 100%)',
        borderRadius: 5,
        p: { xs: 2, md: 6 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(247,250,252,0.9) 100%)',
          borderRadius: 5,
          zIndex: -1,
        }
      }} data-guide="hero-section">
        {/* 가이드 버튼 */}
        <Tooltip title="가이드 보기">
          <IconButton 
            onClick={handleStartGuide} 
            sx={{ 
              position: 'absolute', 
              right: 16, 
              top: 16,
              background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
              color: 'white',
              boxShadow: '0 8px 32px rgba(26, 54, 93, 0.3)',
              display: { xs: 'none', md: 'flex' },
              '&:hover': {
                background: 'linear-gradient(135deg, #153E75 0%, #1A365D 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(26, 54, 93, 0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            data-guide="guide-button"
            className="guide-button-desktop-only"
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>

        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Typography variant="h1" component="h1" gutterBottom sx={{ 
            fontWeight: 900,
            fontSize: { xs: '1.6rem', sm: '2.8rem', md: '3.2rem', lg: '3.8rem' },
            background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 70%, #D69E2E 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: 'none',
            mb: { xs: 1.5, md: 3 },
            letterSpacing: '-0.02em',
            lineHeight: 1.2
          }} data-guide="main-title">
            문서 검색은 이제 AI에게 말기세요.
          </Typography>

          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#2D3748',
            mb: { xs: 1.5, md: 3 },
            fontSize: { xs: '0.95rem', sm: '1.4rem', md: '1.8rem' }
          }}>
            사내 문서, DB, 웹페이지를 AI가 학습해서 질문에 정확히 답합니다.
          </Typography>
          
          <Typography variant="h6" color="text.secondary" paragraph sx={{ 
            maxWidth: '700px', 
            margin: '0 auto', 
            lineHeight: 1.8,
            mb: { xs: 2.5, md: 5 },
            fontSize: { xs: '0.85rem', md: '1.2rem' },
            fontWeight: 600,
            color: '#4A5568'
          }}>
            <strong style={{ color: '#D69E2E' }}>엔터프라이즈급 설치형 · 완전한 소스코드 제공 · 1-3일 도입 가능</strong>
          </Typography>

          <Grid container spacing={{ xs: 1.5, md: 3 }} justifyContent="center" sx={{ mb: { xs: 2.5, md: 5 } }} data-guide="key-features">
            {[
              { icon: <DocumentScannerIcon sx={{ fontSize: { xs: 24, md: 40 } }} />, text: '매뉴얼, 업무가이드, 내부문서 자동 학습', color: '#1A365D' },
              { icon: <AutoAwesomeIcon sx={{ fontSize: { xs: 24, md: 40 } }} />, text: 'OpenAI Large 모델로 초정밀 답변', color: '#2D3748' },
              { icon: <SpeedIcon sx={{ fontSize: { xs: 24, md: 40 } }} />, text: '하이브리드 검색으로 20-30% 정확도 향상', color: '#38A169' },
              { icon: <SecurityIcon sx={{ fontSize: { xs: 24, md: 40 } }} />, text: '보안 강화형 설치형 구조 (내부망에서도 작동)', color: '#D69E2E' }
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.95) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: `2px solid ${feature.color}15`,
                  borderRadius: 4,
                  p: { xs: 1, md: 2 },
                  boxShadow: `0 8px 32px ${feature.color}10`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 16px 48px ${feature.color}20`,
                    borderColor: `${feature.color}30`,
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    <Box sx={{ 
                      color: feature.color, 
                      mr: { xs: 0, md: 2 }, 
                      mb: { xs: 0.5, md: 0 },
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', md: '1.1rem' },
                      textAlign: { xs: 'center', md: 'left' },
                      lineHeight: 1.3,
                      color: '#2D3748'
                    }}>
                      {feature.text}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, md: 3 }} justifyContent="center" sx={{ mt: { xs: 2, md: 4 } }}>
            <Button 
              variant="contained" 
              size="large" 
              startIcon={<AutoAwesomeIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />}
              onClick={() => navigate('/chat')}
              sx={{ 
                py: { xs: 1.5, md: 2.5 },
                px: { xs: 3, md: 6 }, 
                fontSize: { xs: '0.9rem', md: '1.3rem' },
                fontWeight: 'bold',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
                boxShadow: '0 8px 32px rgba(26, 54, 93, 0.4)',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #153E75 0%, #1A365D 100%)',
                  boxShadow: '0 12px 40px rgba(26, 54, 93, 0.5)',
                  transform: 'translateY(-3px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              data-guide="start-chat"
            >
              무료 체험 신청
            </Button>
            
            <Button 
              variant="outlined" 
              size="large" 
              startIcon={<BusinessCenterIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />}
              onClick={handleContactModalOpen}
              sx={{ 
                py: { xs: 1.5, md: 2.5 },
                px: { xs: 3, md: 6 }, 
                fontSize: { xs: '0.9rem', md: '1.3rem' },
                fontWeight: 'bold',
                borderRadius: '12px',
                borderWidth: 2,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderColor: '#D69E2E',
                color: '#B7791F',
                boxShadow: '0 8px 32px rgba(214, 158, 46, 0.2)',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #D69E2E 0%, #B7791F 100%)',
                  color: 'white',
                  borderColor: '#D69E2E',
                  boxShadow: '0 12px 40px rgba(214, 158, 46, 0.3)',
                  transform: 'translateY(-3px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              data-guide="contact-button"
            >
              도입 상담 요청
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Section 2: 문제 정의 + 해결 제안 - 엔터프라이즈 디자인 */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2, md: 6 }, 
        mb: { xs: 3, md: 6 }, 
        background: 'linear-gradient(135deg, rgba(247, 250, 252, 0.9) 0%, rgba(237, 242, 247, 0.9) 100%)',
        borderRadius: 5,
        border: '1px solid rgba(113, 128, 150, 0.1)',
        boxShadow: '0 16px 48px rgba(45, 55, 72, 0.08)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(135deg, #E53E3E 0%, #1A365D 100%)',
        }
      }} data-guide="problem-solution">
        <Typography variant="h3" gutterBottom sx={{ 
          textAlign: 'center', 
          fontWeight: 800,
          mb: { xs: 1, md: 2 },
          background: 'linear-gradient(135deg, #E53E3E 0%, #1A365D 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' }
        }}>
          반복되는 질문, 정리 안된 자료...
        </Typography>
        
        <Typography variant="h4" sx={{ 
          textAlign: 'center', 
          fontWeight: 700,
          mb: { xs: 3, md: 6 },
          color: '#2D3748',
          fontSize: { xs: '1rem', sm: '1.6rem', md: '2rem' }
        }}>
          AI가 대신 정리하고 응답합니다.
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(254, 215, 215, 0.4) 0%, rgba(255, 245, 245, 0.6) 100%)',
              border: '2px solid rgba(229, 62, 62, 0.15)',
              borderRadius: 4,
              p: { xs: 1.5, md: 3 },
              height: '100%',
              boxShadow: '0 12px 40px rgba(229, 62, 62, 0.08)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 60px rgba(229, 62, 62, 0.12)',
              }
            }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                mb: { xs: 1.5, md: 3 }, 
                color: '#C53030',
                fontSize: { xs: '1rem', md: '1.5rem' }
              }}>
                기업 문제점
              </Typography>
              <List>
                {[
                  { text: '자료가 많아도 찾기 어려움', emoji: '😩' },
                  { text: '매번 같은 질문 반복', emoji: '😩' },
                  { text: '문서마다 사람이 다르게 해석', emoji: '😩' },
                  { text: '외부 SaaS 보안 우려', emoji: '🔴' }
                ].map((item, index) => (
                  <ListItem key={index} sx={{ py: { xs: 0.5, md: 1 } }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: 'rgba(254, 215, 215, 0.8)', 
                        width: { xs: 32, md: 40 }, 
                        height: { xs: 32, md: 40 },
                        fontSize: { xs: '1rem', md: '1.2rem' }
                      }}>
                        {item.emoji}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', md: '1.1rem' },
                        color: '#2D3748'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(190, 227, 248, 0.4) 0%, rgba(235, 248, 255, 0.6) 100%)',
              border: '2px solid rgba(26, 54, 93, 0.15)',
              borderRadius: 4,
              p: { xs: 1.5, md: 3 },
              height: '100%',
              boxShadow: '0 12px 40px rgba(26, 54, 93, 0.08)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 60px rgba(26, 54, 93, 0.12)',
              }
            }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                mb: { xs: 1.5, md: 3 }, 
                color: '#1A365D',
                fontSize: { xs: '1rem', md: '1.5rem' }
              }}>
                우리 시스템 해결
              </Typography>
              <List>
                {[
                  { text: 'OpenAI Large 모델로 초정밀 검색', emoji: '🔍' },
                  { text: '하이브리드 검색 (키워드+의미)', emoji: '⚡' },
                  { text: 'Redis 캐싱으로 응답 속도 90% 향상', emoji: '🚀' },
                  { text: '통일된 지식베이스 기반 응답', emoji: '📚' },
                  { text: '설치형 + 소스코드 + 실시간 모니터링', emoji: '👑' }
                ].map((item, index) => (
                  <ListItem key={index} sx={{ py: { xs: 0.5, md: 1 } }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: 'rgba(190, 227, 248, 0.8)', 
                        width: { xs: 32, md: 40 }, 
                        height: { xs: 32, md: 40 },
                        fontSize: { xs: '1rem', md: '1.2rem' }
                      }}>
                        {item.emoji}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', md: '1.1rem' },
                        color: '#2D3748'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 3: 주요 고객 타겟 - 엔터프라이즈 프리미엄 디자인 */}
      <Box sx={{ mb: { xs: 4, md: 8 } }} data-guide="target-customers">
        <Typography variant="h3" gutterBottom sx={{ 
          textAlign: 'center', 
          fontWeight: 800,
          mb: { xs: 1.5, md: 3 },
          background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' }
        }}>
          어떤 조직이 사용하면 좋을까요?
        </Typography>
        
        <Typography variant="h4" gutterBottom sx={{ 
          textAlign: 'center', 
          fontWeight: 700,
          mb: { xs: 2.5, md: 5 },
          color: '#2D3748',
          fontSize: { xs: '1rem', sm: '1.6rem', md: '2rem' }
        }}>
          🏆 엔터프라이즈 고객
        </Typography>
        
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          {[
            {
              icon: <BusinessIcon sx={{ fontSize: { xs: 36, md: 56 } }} />,
              title: 'IT 스타트업',
              description: '문서/이슈 정리 자동화',
              color: '#1A365D',
              bgGradient: 'linear-gradient(135deg, rgba(26, 54, 93, 0.08) 0%, rgba(26, 54, 93, 0.03) 100%)',
              shadowColor: 'rgba(26, 54, 93, 0.15)'
            },
            {
              icon: <GroupsIcon sx={{ fontSize: { xs: 36, md: 56 } }} />,
              title: '중소기업',
              description: '매뉴얼/지침서 자동 응답 시스템',
              color: '#2D3748',
              bgGradient: 'linear-gradient(135deg, rgba(45, 55, 72, 0.08) 0%, rgba(45, 55, 72, 0.03) 100%)',
              shadowColor: 'rgba(45, 55, 72, 0.15)'
            },
            {
              icon: <SchoolIcon sx={{ fontSize: { xs: 36, md: 56 } }} />,
              title: '대학/연구소',
              description: '논문·실험기록 Q&A',
              color: '#4A5568',
              bgGradient: 'linear-gradient(135deg, rgba(74, 85, 104, 0.08) 0%, rgba(74, 85, 104, 0.03) 100%)',
              shadowColor: 'rgba(74, 85, 104, 0.15)'
            },
            {
              icon: <PersonIcon sx={{ fontSize: { xs: 36, md: 56 } }} />,
              title: '프리랜서/강사',
              description: '자료 기반 상담 챗봇',
              color: '#D69E2E',
              bgGradient: 'linear-gradient(135deg, rgba(214, 158, 46, 0.08) 0%, rgba(214, 158, 46, 0.03) 100%)',
              shadowColor: 'rgba(214, 158, 46, 0.15)'
            },
            {
              icon: <GavelIcon sx={{ fontSize: { xs: 36, md: 56 } }} />,
              title: '전문직',
              description: '계약서/사례 자동 응답',
              color: '#718096',
              bgGradient: 'linear-gradient(135deg, rgba(113, 128, 150, 0.08) 0%, rgba(113, 128, 150, 0.03) 100%)',
              shadowColor: 'rgba(113, 128, 150, 0.15)'
            }
          ].map((customer, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card sx={{ 
                height: '100%',
                textAlign: 'center',
                background: customer.bgGradient,
                border: `2px solid ${customer.color}15`,
                borderRadius: 4,
                p: { xs: 1.5, md: 3 },
                boxShadow: `0 12px 40px ${customer.shadowColor.replace('0.15', '0.08')}`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-12px) scale(1.02)',
                  boxShadow: `0 20px 60px ${customer.shadowColor}`,
                  borderColor: `${customer.color}25`,
                },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(135deg, ${customer.color} 0%, ${customer.color}80 100%)`,
                }
              }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ mb: { xs: 1, md: 2 }, color: customer.color, position: 'relative' }}>
                    <CheckCircleIcon sx={{ 
                      fontSize: { xs: 20, md: 28 }, 
                      position: 'absolute',
                      top: { xs: -6, md: -8 },
                      right: { xs: -6, md: -8 },
                      color: '#38A169',
                      background: 'white',
                      borderRadius: '50%'
                    }} />
                    <Box sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 48, md: 72 },
                      height: { xs: 48, md: 72 },
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${customer.color} 0%, ${customer.color}CC 100%)`,
                      color: 'white',
                      mb: { xs: 1, md: 2 },
                      boxShadow: `0 8px 24px ${customer.shadowColor}`
                    }}>
                      {customer.icon}
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 0.5, md: 1 }, 
                    color: customer.color,
                    fontSize: { xs: '0.9rem', md: '1.2rem' }
                  }}>
                    {customer.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#2D3748',
                    fontWeight: 500,
                    fontSize: { xs: '0.75rem', md: '0.95rem' },
                    lineHeight: 1.4
                  }}>
                    {customer.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Section 4: 시스템 도입 효과 - 엔터프라이즈 프리미엄 디자인 */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2.5, md: 6 }, 
        mb: { xs: 4, md: 8 }, 
        background: 'linear-gradient(135deg, #1A365D 0%, #2C5282 100%)',
        color: 'white', 
        borderRadius: 5,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(26, 54, 93, 0.3)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5,
        }
      }} data-guide="implementation-effects">
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" gutterBottom sx={{ 
            textAlign: 'center', 
            fontWeight: 800,
            mb: { xs: 3, md: 6 },
            background: 'linear-gradient(135deg, #FBD38D 0%, #D69E2E 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' }
          }}>
            도입 후 달라지는 것들
          </Typography>
          
          <Grid container spacing={{ xs: 2, md: 4 }}>
            {[
              { icon: <AccessTimeIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, title: '응답 시간 90% 단축', subtitle: 'Redis 캐싱으로 즉시 답변' },
              { icon: <SpeedIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, title: '검색 속도 100배 향상', subtitle: 'HNSW 인덱스 적용' },
              { icon: <TrendingUpIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, title: '검색 정확도 20-30% 향상', subtitle: 'BM25 + Vector 하이브리드' },
              { icon: <MonetizationOnIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, title: 'API 비용 50-70% 절감', subtitle: '똑똑한 캐싱 시스템' }
            ].map((effect, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 4,
                  p: { xs: 1.5, md: 3 },
                  textAlign: 'center',
                  height: '100%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    background: 'rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
                  }
                }}>
                  <Box sx={{ mb: { xs: 1, md: 2 }, color: 'white' }}>
                    {effect.icon}
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 0.5, md: 1 },
                    color: 'white',
                    fontSize: { xs: '0.9rem', md: '1.2rem' },
                    lineHeight: 1.3
                  }}>
                    {effect.title}
                  </Typography>
                  {effect.subtitle && (
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: { xs: '0.75rem', md: '0.95rem' }
                    }}>
                      {effect.subtitle}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: { xs: 3, md: 6 } }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              mb: { xs: 2, md: 4 }, 
              textAlign: 'center',
              color: 'white',
              fontSize: { xs: '1.1rem', md: '1.6rem' }
            }}>
              기업 고객 후기
            </Typography>
            <Grid container spacing={{ xs: 2, md: 4 }}>
              {[
                { quote: '"2시간 걸리던 업무가 10분 만에 해결됩니다. 투자 대비 효과가 탁월합니다."', author: '- IT 스타트업 CTO', rating: '★★★★★' },
                { quote: '"실험노트 자동 정리로 연구 속도 10배 향상! 매우 만족합니다."', author: '- K대 연구소 책임연구원', rating: '★★★★★' }
              ].map((testimonial, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card sx={{ 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 4,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: 'rgba(255, 255, 255, 0.15)',
                    }
                  }}>
                    <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
                      <Typography variant="body1" sx={{ 
                        fontStyle: 'italic', 
                        mb: { xs: 1, md: 2 }, 
                        color: 'white',
                        fontSize: { xs: '0.9rem', md: '1.1rem' },
                        lineHeight: 1.6
                      }}>
                        {testimonial.quote}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.75rem', md: '0.9rem' }
                        }}>
                          {testimonial.author}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: '#FBD38D',
                          fontSize: { xs: '0.9rem', md: '1.1rem' }
                        }}>
                          {testimonial.rating}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Section 5: 도입 프로세스 - 엔터프라이즈 프리미엄 디자인 */}
      <Box sx={{ mb: { xs: 4, md: 8 } }} data-guide="implementation-process">
        <Typography variant="h3" gutterBottom sx={{ 
          textAlign: 'center', 
          fontWeight: 800,
          mb: { xs: 3, md: 6 },
          background: 'linear-gradient(135deg, #B7791F 0%, #D69E2E 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' }
        }}>
          도입까지 단 3단계, 최대 3일 소요
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {[
            { 
              step: '1', 
              title: '운영 시연 및 테스트', 
              subtitle: '사내 문서로 직접 확인 가능',
              color: '#2D3748',
              gradient: 'linear-gradient(135deg, #2D3748 0%, #4A5568 100%)',
              shadowColor: 'rgba(45, 55, 72, 0.3)'
            },
            { 
              step: '2', 
              title: '계약 및 환경 설정', 
              subtitle: '온프레미스 설치 / 내부망 구성 지원',
              color: '#1A365D',
              gradient: 'linear-gradient(135deg, #1A365D 0%, #2C5282 100%)',
              shadowColor: 'rgba(26, 54, 93, 0.3)'
            },
            { 
              step: '3', 
              title: '설치 및 운영 시작', 
              subtitle: '초기 데이터 업로드, 관리자 가이드 제공',
              color: '#D69E2E',
              gradient: 'linear-gradient(135deg, #D69E2E 0%, #B7791F 100%)',
              shadowColor: 'rgba(214, 158, 46, 0.3)'
            }
          ].map((process, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ 
                position: 'relative',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.95) 100%)',
                borderRadius: 5,
                p: { xs: 2, md: 4 },
                height: '100%',
                border: `3px solid ${process.color}15`,
                boxShadow: `0 12px 40px ${process.shadowColor.replace('0.3', '0.15')}`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-12px)',
                  boxShadow: `0 20px 60px ${process.shadowColor}`,
                  borderColor: `${process.color}25`,
                }
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: { xs: -15, md: -20 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: 48, md: 60 },
                  height: { xs: 48, md: 60 },
                  borderRadius: '50%',
                  background: process.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: { xs: '1.4rem', md: '2rem' },
                  fontWeight: 'bold',
                  boxShadow: `0 8px 24px ${process.shadowColor}`
                }}>
                  {process.step}
                </Box>
                
                <Box sx={{ mt: { xs: 2.5, md: 4 }, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 1, md: 2 },
                    color: process.color,
                    fontSize: { xs: '1rem', md: '1.4rem' }
                  }}>
                    {process.title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#4A5568',
                    fontWeight: 500,
                    lineHeight: 1.6,
                    fontSize: { xs: '0.8rem', md: '1rem' }
                  }}>
                    {process.subtitle}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Section 6: 기술 구성 - 엔터프라이즈 프리미엄 디자인 */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2.5, md: 6 }, 
        mb: { xs: 4, md: 8 }, 
        background: 'linear-gradient(135deg, rgba(247, 250, 252, 0.9) 0%, rgba(237, 242, 247, 0.9) 100%)',
        borderRadius: 5,
        border: '1px solid rgba(113, 128, 150, 0.1)'
      }} data-guide="tech-stack">
        <Typography variant="h3" gutterBottom sx={{ 
          textAlign: 'center', 
          fontWeight: 800,
          mb: { xs: 3, md: 6 },
          background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' }
        }}>
          기술 구성 (엔터프라이즈급 신뢰성)
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {[
            { tech: 'FastAPI + React', desc: '고속 백엔드 + 반응형 프론트', icon: <StorageIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#1A365D' },
            { tech: 'OpenAI Large 임베딩', desc: '3072차원 초정밀 벡터 검색', icon: <AutoAwesomeIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#2D3748' },
            { tech: 'FAISS HNSW 인덱스', desc: '100배 빠른 벡터 검색 엔진', icon: <SpeedIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#38A169' },
            { tech: 'BM25 하이브리드', desc: '키워드+의미 검색 결합', icon: <SearchIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#4A5568' },
            { tech: 'Redis 캐싱', desc: '빠른 응답 + API 비용 절감', icon: <TrendingUpIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#E53E3E' },
            { tech: 'Prometheus 모니터링', desc: '실시간 성능 추적 및 분석', icon: <BuildIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#D69E2E' },
            { tech: '문서지원', desc: 'PDF, HWP, Word, Excel, PPT 등', icon: <InsertDriveFileIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#718096' },
            { tech: '카카오톡 연동', desc: '채널 챗봇, 알림톡 자동응답', icon: <ChatIcon sx={{ fontSize: { xs: 32, md: 48 } }} />, color: '#B7791F' }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
              <Card sx={{ 
                height: '100%',
                textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.95) 100%)',
                border: `2px solid ${item.color}15`,
                borderRadius: 4,
                p: { xs: 1.5, md: 3 },
                boxShadow: `0 12px 40px ${item.color}10`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: `0 20px 60px ${item.color}20`,
                  borderColor: `${item.color}25`,
                },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}80 100%)`,
                }
              }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 48, md: 72 },
                    height: { xs: 48, md: 72 },
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}CC 100%)`,
                    color: 'white',
                    mb: { xs: 1.5, md: 3 },
                    boxShadow: `0 8px 24px ${item.color}25`
                  }}>
                    {item.icon}
                  </Box>
                  
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    color: item.color,
                    mb: { xs: 1, md: 2 },
                    fontSize: { xs: '0.9rem', md: '1.2rem' }
                  }}>
                    {item.tech}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#4A5568',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    fontSize: { xs: '0.75rem', md: '0.95rem' }
                  }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Section 7: 소스코드 제공의 장점 - 엔터프라이즈 프리미엄 디자인 */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2.5, md: 6 }, 
        mb: { xs: 3, md: 6 }, 
        background: 'linear-gradient(135deg, #2D3748 0%, #1A202C 100%)',
        borderRadius: 5,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(45, 55, 72, 0.3)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.02"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5,
        }
      }} data-guide="source-code-benefits">
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" gutterBottom sx={{ 
            textAlign: 'center', 
            fontWeight: 800,
            mb: { xs: 2.5, md: 5 },
            background: 'linear-gradient(135deg, #FBD38D 0%, #D69E2E 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' }
          }}>
            완전한 소스코드 제공의 가치
          </Typography>
          
          <Grid container spacing={{ xs: 2, md: 4 }}>
            {[
              { icon: <BuildIcon sx={{ fontSize: { xs: 24, md: 32 } }} />, text: '자체 수정 가능 → 내부 개발팀 연동', color: '#FBD38D' },
              { icon: <VerifiedIcon sx={{ fontSize: { xs: 24, md: 32 } }} />, text: '외부 의존 없는 완전한 독립성', color: '#9AE6B4' },
              { icon: <BusinessIcon sx={{ fontSize: { xs: 24, md: 32 } }} />, text: '장기적 자산화 + 투자 보호', color: '#A78BFA' }
            ].map((advantage, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  border: `2px solid ${advantage.color}20`,
                  borderRadius: 4,
                  p: { xs: 1.5, md: 3 },
                  height: '100%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    background: 'rgba(255, 255, 255, 0.12)',
                    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
                    borderColor: `${advantage.color}40`,
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ color: advantage.color, mr: { xs: 1, md: 2 } }}>
                      {advantage.icon}
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: 'white',
                      textAlign: 'center',
                      fontSize: { xs: '0.85rem', md: '1.2rem' },
                      lineHeight: 1.3
                    }}>
                      {advantage.text}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Typography variant="body1" sx={{ 
            textAlign: 'center', 
            mt: { xs: 2, md: 4 },
            fontSize: { xs: '0.85rem', md: '1.1rem' }, 
            lineHeight: 1.8,
            color: '#E2E8F0'
          }}>
            ※ 기업 개발팀을 위한 <code style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              padding: '2px 6px', 
              borderRadius: '4px',
              fontSize: { xs: '0.8rem', md: '1rem' },
              color: '#FBD38D'
            }}>.env</code>, <code style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              padding: '2px 6px', 
              borderRadius: '4px',
              color: '#FBD38D'
            }}>.py</code>, <code style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              padding: '2px 6px', 
              borderRadius: '4px',
              color: '#FBD38D' 
            }}>.jsx</code> 전체 아키텍처 문서 제공
          </Typography>
        </Box>
      </Paper>

      {/* Section 8: 지금 바로 시작하세요 - 엔터프라이즈 프리미엄 CTA 섹션 */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2.5, md: 6 }, 
        mb: { xs: 3, md: 6 }, 
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 250, 252, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderRadius: 5,
        textAlign: 'center',
        border: '3px solid',
        borderImage: 'linear-gradient(135deg, #1A365D 0%, #D69E2E 50%, #2D3748 100%) 1',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(26, 54, 93, 0.15)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(26, 54, 93, 0.02) 0%, rgba(214, 158, 46, 0.02) 50%, rgba(45, 55, 72, 0.02) 100%)',
          zIndex: -1,
        }
      }} data-guide="cta-section">
        <Typography variant="h3" gutterBottom sx={{ 
          fontWeight: 900,
          background: 'linear-gradient(135deg, #1A365D 0%, #D69E2E 50%, #2D3748 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: { xs: 2, md: 4 },
          fontSize: { xs: '1.4rem', sm: '2.2rem', md: '2.8rem' },
          letterSpacing: '-0.02em'
        }}>
          엔터프라이즈급 솔루션을 지금 경험하세요
        </Typography>
        
        <Grid container spacing={{ xs: 1.5, md: 3 }} justifyContent="center" sx={{ mb: { xs: 2.5, md: 5 } }}>
          {[
            '기업 전용 데모 환경 제공',
            '전담 기술 지원팀 배정',
            '보안/규정 준수 가이드 제공'
          ].map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(56, 175, 80, 0.2)',
                borderRadius: 4,
                p: { xs: 1, md: 2 },
                boxShadow: '0 8px 32px rgba(56, 175, 80, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(56, 175, 80, 0.15)',
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircleIcon sx={{ color: '#38A169', mr: { xs: 0.5, md: 1 }, fontSize: { xs: 18, md: 24 } }} />
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: '#2D3748',
                    fontSize: { xs: '0.8rem', md: '1rem' },
                    textAlign: 'center'
                  }}>
                    {feature}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, md: 3 }} justifyContent="center">
          <Button 
            variant="contained" 
            size="large"
            startIcon={<SearchIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />}
            onClick={() => navigate('/chat')}
            sx={{ 
              py: { xs: 2, md: 3 },
              px: { xs: 4, md: 7 }, 
              fontSize: { xs: '0.9rem', md: '1.3rem' },
              fontWeight: 'bold',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
              boxShadow: '0 12px 48px rgba(26, 54, 93, 0.4)',
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'linear-gradient(135deg, #153E75 0%, #1A365D 100%)',
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 16px 56px rgba(26, 54, 93, 0.5)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.6s',
              },
              '&:hover::before': {
                left: '100%',
              },
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            엔터프라이즈 체험 신청
          </Button>
          
          <Button 
            variant="outlined" 
            size="large" 
            startIcon={<CloudDownloadIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />}
            onClick={handleContactModalOpen}
            sx={{ 
              py: { xs: 2, md: 3 },
              px: { xs: 4, md: 7 }, 
              fontSize: { xs: '0.9rem', md: '1.3rem' },
              fontWeight: 'bold',
              borderRadius: '12px',
              borderWidth: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderColor: '#D69E2E',
              color: '#B7791F',
              boxShadow: '0 8px 32px rgba(214, 158, 46, 0.2)',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #D69E2E 0%, #B7791F 100%)',
                color: 'white',
                borderColor: '#D69E2E',
                boxShadow: '0 12px 40px rgba(214, 158, 46, 0.3)',
                transform: 'translateY(-4px) scale(1.02)',
              },
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            기업 제안서 다운로드
          </Button>
        </Stack>
      </Paper>

      {/* Footer - 엔터프라이즈 프리미엄 디자인 */}
      <Box sx={{ 
        textAlign: 'center', 
        py: { xs: 3, md: 5 },
        background: 'linear-gradient(135deg, rgba(247, 250, 252, 0.95) 0%, rgba(237, 242, 247, 0.95) 100%)',
        borderRadius: 5,
        border: '1px solid rgba(113, 128, 150, 0.1)',
        mx: 1,
        boxShadow: '0 8px 32px rgba(45, 55, 72, 0.08)'
      }} data-guide="footer">
        <Typography variant="h5" gutterBottom sx={{ 
          fontWeight: 700, 
          background: 'linear-gradient(135deg, #1A365D 0%, #2D3748 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: { xs: 1.5, md: 2 },
          fontSize: { xs: '1rem', md: '1.5rem' },
          lineHeight: 1.4
        }}>
          "기업의 지식 자산을 AI와 함께<br/>차세대 경쟁력으로 전환하세요."
        </Typography>
        <Divider sx={{ my: { xs: 2, md: 3 }, mx: 4, borderColor: 'rgba(113, 128, 150, 0.2)' }} />
        <Typography variant="body2" sx={{ 
          color: '#4A5568',
          fontWeight: 600,
          fontSize: { xs: '0.8rem', md: '1rem' }
        }}>
          엔터프라이즈 AI 질의응답 시스템 | © 2025 BuildUp KOREA | Enterprise Solutions
        </Typography>
      </Box>

      {/* 도입 상담 문의 모달 */}
      <ContactModal 
        open={contactModalOpen} 
        onClose={handleContactModalClose}
        contactInfo={contactInfo}
      />

      {/* 프로모션 모달 */}
      <PromotionModal 
        open={promotionModalOpen} 
        onClose={handlePromotionModalClose}
        onContactClick={handlePromotionContactClick}
      />
    </Container>
  );
};

export default HomePage;